/**
 * Public pricing page (/pricing), readable without an account. Two tabs keep it
 * short: the credit packs on sale (price, currency, what they buy) and the
 * credit cost of every action. The purchase terms stay visible as three short
 * facts under the packs, with the full Merchant-of-Record / tax / refund / age
 * wording one click away.
 * Buying opens CheckoutModal — our order summary beside Paddle's inline checkout
 * frame — for a transaction the API created; a Paddle payment link
 * (/pricing?_ptxn=…) opens the same modal. The credits arrive through Paddle's
 * webhook, so after a completed checkout the page polls the balance for a while.
 */
import { useEffect, useRef, useState } from "react";
import { ApiError, createCheckout, loadCreditPrices, type CreditPack, type CreditPricesResult } from "../apiClient.js";
import { AsyncButton } from "../components/AsyncButton.js";
import { CheckoutModal, type CheckoutSummary } from "../components/CheckoutModal.js";
import { CreditIcon } from "../components/CreditIcon.js";
import { track } from "../lib/analytics.js";
import { formatCents, formatCreditNumber, formatCreditUnit, formatCredits } from "../lib/credits.js";
import { withInlineNodes } from "../lib/inlineNodes.js";
import { useLanguage, useT, type TranslationKey } from "../lib/i18n/store.js";
import {
  blockedBillingCountry,
  clearPaymentLinkFromUrl,
  loadPaddle,
  onPaddleEvent,
  paddleClientToken,
  paymentLinkTransaction as paymentLinkInUrl
} from "../lib/paddle.js";
import type { Page } from "../types.js";
import { NOVEL_TRANSLATION_ENABLED } from "../lib/features.js";

/** The actions listed in the "what things cost" tab, in display order. */
const COST_ROWS: { operation: string; labelKey: TranslationKey; textOnly?: string }[] = [
  // Each size also names its price without pictures ("Generate images" off).
  { operation: "generate_novel_short", labelKey: "pricing.op.generateShort", textOnly: "generate_novel_short_text" },
  { operation: "generate_novel", labelKey: "pricing.op.generate", textOnly: "generate_novel_text" },
  { operation: "generate_novel_long", labelKey: "pricing.op.generateLong", textOnly: "generate_novel_long_text" },
  { operation: "novel_emotions", labelKey: "pricing.op.novelEmotions" },
  { operation: "suggest_idea", labelKey: "pricing.op.idea" },
  { operation: "extend_novel", labelKey: "pricing.op.extend" },
  ...(NOVEL_TRANSLATION_ENABLED ? [{ operation: "translate_novel", labelKey: "pricing.op.translate" as const }] : []),
  { operation: "ai_edit_plan", labelKey: "pricing.op.plan" },
  { operation: "ai_edit_apply", labelKey: "pricing.op.apply" },
  { operation: "regenerate_cover", labelKey: "pricing.op.image" },
  { operation: "regenerate_portrait", labelKey: "pricing.op.portrait" },
  { operation: "character_emotions", labelKey: "pricing.op.characterEmotions" },
  { operation: "generate_avatar", labelKey: "pricing.op.avatar" }
];

/** Seconds after a completed checkout at which the balance is re-read. */
const BALANCE_POLL_SECONDS = [2, 5, 10, 20];

export type PricingTab = "packs" | "costs";

export function PricingPage({
  signedIn,
  userEmail = null,
  balanceCents = null,
  balanceLoading = false,
  initialTab = "packs",
  onNavigate,
  onSignIn,
  onPurchased,
  loadPrices = loadCreditPrices
}: {
  signedIn: boolean;
  userEmail?: string | null;
  balanceCents?: number | null;
  /** The balance's first read is still in flight (its card shows a placeholder). */
  balanceLoading?: boolean;
  initialTab?: PricingTab;
  onNavigate: (page: Page) => void;
  /** Called when a signed-out visitor picks a pack (Buy): take them to sign up. */
  onSignIn: () => void;
  /** Called (repeatedly, for a short while) after a completed checkout to refresh the balance. */
  onPurchased: () => void;
  /** Injectable for tests. */
  loadPrices?: () => Promise<CreditPricesResult>;
}) {
  const t = useT();
  const lang = useLanguage();
  const [tab, setTab] = useState<PricingTab>(initialTab);
  const [pricing, setPricing] = useState<CreditPricesResult | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchased, setPurchased] = useState(false);
  const [regionBlocked, setRegionBlocked] = useState(false);
  // The open checkout (null = modal closed). A payment link's transaction is
  // read from the URL on the first render and opened here once checkout is live.
  const [checkout, setCheckout] = useState<{ transactionId: string; summary: CheckoutSummary | null } | null>(null);
  const [paymentLinkTransaction, setPaymentLinkTransaction] = useState<string | null>(paymentLinkInUrl);
  // The pack of the checkout last opened, for the analytics events (null for a
  // payment link, whose transaction names no pack of ours).
  const checkoutPackRef = useRef<string | null>(null);
  // Before any checkout opens the buyer (1) confirms they are 18+ and accept the
  // Terms and Refund Policy and (2) expressly asks for the service to start at
  // once, acknowledging that a withdrawal then refunds only unused credits (EU/UK
  // consumer law). The Buy buttons stay live so a click without them points at
  // the boxes instead of silently doing nothing.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [immediateStart, setImmediateStart] = useState(false);
  const [consentMissing, setConsentMissing] = useState(false);
  const consentBoxRef = useRef<HTMLInputElement>(null);
  const consentsGiven = termsAccepted && immediateStart;

  useEffect(() => {
    let active = true;
    loadPrices().then(
      (result) => active && setPricing(result),
      () => active && setLoadFailed(true)
    );
    return () => {
      active = false;
    };
  }, [loadPrices]);

  // Drop `_ptxn` from the URL before Paddle.js loads (the effect below, after the
  // prices): Paddle.js would otherwise auto-open the link as an overlay. Declared
  // first so it runs first; the id is already in state.
  useEffect(() => {
    clearPaymentLinkFromUrl();
  }, []);

  // Initialize Paddle.js as soon as checkout is live, so the Buy click only
  // waits for the transaction.
  const checkoutLive = Boolean(pricing?.checkoutEnabled && paddleClientToken);
  useEffect(() => {
    if (!checkoutLive || !pricing) return;
    loadPaddle(pricing.paddleEnvironment).catch((err: unknown) =>
      setError(err instanceof Error ? err.message : t("pricing.checkoutFailed"))
    );
  }, [checkoutLive, pricing, t]);

  // A payment link (…/pricing?_ptxn=…) opens its transaction in the modal.
  useEffect(() => {
    if (!checkoutLive || !paymentLinkTransaction) return;
    setCheckout({ transactionId: paymentLinkTransaction, summary: null });
    setPaymentLinkTransaction(null);
    checkoutPackRef.current = null;
    track("checkout_started", { pack_id: null });
  }, [checkoutLive, paymentLinkTransaction]);

  // Paddle.js closes a checkout whose billing country Lorvok does not sell to
  // (lib/paddle.ts). The modal says why while it is open; this keeps the reason
  // on the page after it is closed.
  useEffect(
    () =>
      onPaddleEvent((event) => {
        if (blockedBillingCountry(event)) setRegionBlocked(true);
      }),
    []
  );

  useEffect(() => {
    const timers: number[] = [];
    const unsubscribe = onPaddleEvent((event) => {
      if (event.name !== "checkout.completed") return;
      track("checkout_completed", { pack_id: checkoutPackRef.current });
      setPurchased(true);
      for (const seconds of BALANCE_POLL_SECONDS) {
        timers.push(window.setTimeout(onPurchased, seconds * 1000));
      }
    });
    return () => {
      unsubscribe();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [onPurchased]);

  async function buy(packId: string): Promise<void> {
    setError(null);
    setRegionBlocked(false);
    if (!signedIn) {
      onSignIn();
      return;
    }
    if (!pricing) return;
    if (!consentsGiven) {
      setConsentMissing(true);
      consentBoxRef.current?.focus();
      return;
    }
    const index = pricing.packs.findIndex((candidate) => candidate.id === packId);
    const pack = pricing.packs[index];
    if (!pack) return;
    try {
      const { transactionId } = await createCheckout(packId, { termsAccepted, immediateStart });
      setCheckout({
        transactionId,
        summary: {
          title: tierName(index),
          creditsCents: pack.creditsCents,
          bonusPercent: bonusPercentOf(pack),
          priceCents: pack.priceCents,
          currency: pricing.currency
        }
      });
      checkoutPackRef.current = packId;
      track("checkout_started", { pack_id: packId });
    } catch (err) {
      // The payment provider failed on our side: say so in the buyer's language.
      if (err instanceof ApiError && err.code === "payment_unavailable") {
        setError(t("pricing.paymentUnavailable"));
        return;
      }
      setError(err instanceof Error ? err.message : t("pricing.checkoutFailed"));
    }
  }

  // Until the prices answer, the packs, the cost figures and the balance card
  // hold their places with skeletons; everything that is fixed copy (tabs,
  // consents, purchase facts, terms) renders at once, so nothing pops in above
  // the packs when they arrive.
  const pricesLoading = pricing === null && !loadFailed;
  const balancePending = signedIn && balanceCents === null && balanceLoading;
  // The consents only appear when checkout is live, the "unavailable" note when
  // it is not. While the prices load, the client token decides: without one
  // checkout can never be live; with one it is (production's answer).
  const showConsents = signedIn && (pricing ? checkoutLive : pricesLoading && Boolean(paddleClientToken));
  const showUnavailable = signedIn && (pricing ? !checkoutLive : pricesLoading && !paddleClientToken);

  const novelPrice = pricing?.prices.generate_novel ?? null;
  const imagePrice = pricing?.prices.regenerate_cover ?? null;
  const editPrice = pricing?.prices.ai_edit_apply ?? null;
  const packCount = pricing?.packs.length ?? 0;
  // With three or more packs the middle one is highlighted as the suggested pick.
  const featuredIndex = packCount >= 3 ? Math.floor(packCount / 2) : -1;

  /** Smallest pack is the starter, biggest the studio, anything between the creator tier. */
  function tierName(index: number): string {
    if (packCount > 1 && index === 0) return t("pricing.tierStarter");
    if (packCount > 1 && index === packCount - 1) return t("pricing.tierStudio");
    return t("pricing.tierCreator");
  }

  return (
    <section className="page-section pricing-page">
      <div className="pricing-hero">
        <div className="section-heading">
          <p className="eyebrow">{t("pricing.eyebrow")}</p>
          <h1>{t("pricing.heading")}</h1>
          <p className="pricing-lead">{t("pricing.lead")}</p>
        </div>
        {signedIn && balanceCents !== null && (
          <div className="pricing-balance">
            <span className="pricing-balance-label">{t("pricing.balanceLabel")}</span>
            <span className="pricing-balance-amount">
              <CreditIcon size={20} />
              {formatCredits(balanceCents, t)}
            </span>
            {novelPrice ? (
              <span className="pricing-balance-worth">
                {t("pricing.worth", { count: Math.floor(balanceCents / novelPrice) })}
              </span>
            ) : pricesLoading ? (
              <span className="skeleton pricing-skel-balance-worth" aria-hidden="true" />
            ) : null}
          </div>
        )}
        {balancePending && (
          <div className="pricing-balance pricing-balance--skeleton" aria-hidden="true">
            <span className="skeleton pricing-skel-balance-label" />
            <span className="skeleton pricing-skel-balance-amount" />
            <span className="skeleton pricing-skel-balance-worth" />
          </div>
        )}
      </div>

      {purchased && (
        <p className="pricing-notice" role="status">
          {t("pricing.thanks")}
        </p>
      )}
      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
      {regionBlocked && !checkout && (
        <p className="auth-error" role="alert">
          {t("pricing.regionUnavailable")}
        </p>
      )}
      {loadFailed && <p className="auth-error">{t("pricing.loadFailed")}</p>}
      {/* Outside the aria-busy panels: AT may hold back changes inside a busy
          region, and this line is the loading signal (the skeletons are hidden). */}
      {pricesLoading && (
        <p className="sr-only" role="status">
          {t("detail.loading")}
        </p>
      )}

      <div className="pricing-tabs" role="tablist" aria-label={t("pricing.eyebrow")}>
        {(["packs", "costs"] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`pricing-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`pricing-panel-${id}`}
            className={tab === id ? "is-active" : undefined}
            onClick={() => setTab(id)}
          >
            {t(id === "packs" ? "pricing.tabPacks" : "pricing.costsHeading")}
          </button>
        ))}
      </div>

      {tab === "packs" && (
        <div
          role="tabpanel"
          id="pricing-panel-packs"
          aria-labelledby="pricing-tab-packs"
          aria-busy={pricesLoading}
          className="pricing-panel"
        >
          {showConsents && (
            <div className={`pricing-consents${consentMissing && !consentsGiven ? " is-missing" : ""}`}>
              <label className="pricing-age-confirm">
                <input
                  ref={consentBoxRef}
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    setTermsAccepted(event.target.checked);
                    if (event.target.checked && immediateStart) setConsentMissing(false);
                  }}
                />
                <span>
                  {withInlineNodes(t("pricing.consentTerms"), {
                    terms: (
                      <button type="button" className="auth-inline-link" onClick={() => onNavigate("terms")}>
                        {t("auth.termsLink")}
                      </button>
                    ),
                    refund: (
                      <button type="button" className="auth-inline-link" onClick={() => onNavigate("refund")}>
                        {t("pricing.refundLink")}
                      </button>
                    )
                  })}
                </span>
              </label>
              <label className="pricing-age-confirm">
                <input
                  type="checkbox"
                  checked={immediateStart}
                  onChange={(event) => {
                    setImmediateStart(event.target.checked);
                    if (event.target.checked && termsAccepted) setConsentMissing(false);
                  }}
                />
                <span>{t("pricing.consentStart")}</span>
              </label>
              {consentMissing && !consentsGiven && (
                <p className="pricing-age-error" role="alert">
                  {t("pricing.consentRequired")}
                </p>
              )}
            </div>
          )}
          {showUnavailable && <p className="pricing-unavailable">{t("pricing.unavailable")}</p>}

          {pricesLoading && <PricingPacksSkeleton />}
          {pricing && (
            <div className="pricing-packs">
              {pricing.packs.map((pack, index) => {
                const canBuy = pack.purchasable && checkoutLive;
                const featured = index === featuredIndex;
                const bonusPercent = bonusPercentOf(pack);
                return (
                  <article key={pack.id} className={`pricing-pack${featured ? " is-featured" : ""}`}>
                    {featured && <span className="pricing-pack-badge">{t("pricing.recommended")}</span>}
                    <div className="pricing-pack-head">
                      <span className="pricing-pack-tier">{tierName(index)}</span>
                      {bonusPercent > 0 && (
                        <span className="pricing-pack-bonus">{t("pricing.bonus", { percent: bonusPercent })}</span>
                      )}
                    </div>
                    <p className="pricing-pack-credits">
                      <span className="sr-only">{formatCredits(pack.creditsCents, t)}</span>
                      <span className="pricing-pack-figure" aria-hidden="true">
                        <CreditIcon size={30} />
                        {formatCreditNumber(pack.creditsCents, lang)}
                      </span>
                      <span className="pricing-pack-unit" aria-hidden="true">
                        {formatCreditUnit(pack.creditsCents, t)}
                      </span>
                    </p>
                    <div className="pricing-pack-cost">
                      <p className="pricing-pack-price">
                        {formatCents(pack.priceCents)} <span className="pricing-currency">{pricing.currency}</span>
                      </p>
                      <p className="pricing-pack-rate">
                        {t("pricing.oneTime")} ·{" "}
                        {t("pricing.per100", {
                          price: formatCents(Math.round((pack.priceCents / pack.creditsCents) * 100))
                        })}
                      </p>
                    </div>
                    <AsyncButton
                      type="button"
                      className={featured ? "primary-button" : "primary-button pricing-pack-button-quiet"}
                      disabled={signedIn && !canBuy}
                      loadingLabel={t("pricing.opening")}
                      onClick={() => buy(pack.id)}
                    >
                      {t("pricing.buy")}
                    </AsyncButton>
                    {novelPrice || imagePrice || editPrice ? (
                      <div className="pricing-pack-includes">
                        <p className="pricing-pack-includes-label">{t("pricing.enoughFor")}</p>
                        <ul>
                          {novelPrice ? (
                            <li>{t("pricing.worth", { count: Math.floor(pack.creditsCents / novelPrice) })}</li>
                          ) : null}
                          {imagePrice ? (
                            <li>{t("pricing.worthImages", { count: Math.floor(pack.creditsCents / imagePrice) })}</li>
                          ) : null}
                          {editPrice ? (
                            <li>{t("pricing.worthEdits", { count: Math.floor(pack.creditsCents / editPrice) })}</li>
                          ) : null}
                        </ul>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          )}

          <ul className="pricing-highlights">
            <li>{t("pricing.factOneTime")}</li>
            <li>{t("pricing.factNoExpiry")}</li>
            <li>{t("pricing.factRefund")}</li>
          </ul>

          <details className="pricing-terms">
            <summary>{t("pricing.termsToggle")}</summary>
            <ul>
              <li>{t("pricing.termNoSubscription")}</li>
              <li>{t("pricing.termCredits")}</li>
              <li>{t("pricing.termMor")}</li>
              <li>{t("pricing.termTax")}</li>
              <li>
                {t("pricing.termRefund")}{" "}
                <button type="button" className="auth-inline-link" onClick={() => onNavigate("refund")}>
                  {t("footer.refund")}
                </button>
              </li>
              <li>{t("pricing.termAge")}</li>
            </ul>
          </details>
        </div>
      )}

      {tab === "costs" && (
        <div
          role="tabpanel"
          id="pricing-panel-costs"
          aria-labelledby="pricing-tab-costs"
          aria-busy={pricesLoading}
          className="pricing-panel"
        >
          {(pricing || pricesLoading) && (
            <table className="pricing-costs">
              <tbody>
                <tr>
                  <th scope="row">{t("pricing.op.read")}</th>
                  <td className="pricing-free">{t("pricing.free")}</td>
                </tr>
                {/* While loading, every action's row is laid out with a placeholder
                    for its price, so the table doesn't grow when they arrive. */}
                {(pricing
                  ? COST_ROWS.filter((row) => typeof pricing.prices[row.operation] === "number")
                  : COST_ROWS
                ).map((row) => (
                  <tr key={row.operation}>
                    <th scope="row">{t(row.labelKey)}</th>
                    <td>
                      {pricing ? (
                        <>
                          <span className="pricing-cost">
                            <CreditIcon size={16} />
                            <span>{formatCredits(pricing.prices[row.operation], t)}</span>
                          </span>
                          {row.textOnly && typeof pricing.prices[row.textOnly] === "number" ? (
                            <span className="pricing-cost-alt">
                              {t("pricing.withoutImages", { price: formatCredits(pricing.prices[row.textOnly], t) })}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="skeleton pricing-skel-cost" aria-hidden="true" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="pricing-costs-foot">{t("pricing.failedRefunded")}</p>
        </div>
      )}

      {checkout && pricing && (
        <CheckoutModal
          environment={pricing.paddleEnvironment}
          transactionId={checkout.transactionId}
          email={userEmail}
          summary={checkout.summary}
          onClose={() => setCheckout(null)}
        />
      )}
    </section>
  );
}

/** Credits above the list rate (1 credit = 1 cent), in percent: the pack's bonus. */
function bonusPercentOf(pack: CreditPack): number {
  return Math.round(((pack.creditsCents - pack.priceCents) / pack.priceCents) * 100);
}

/**
 * The pack cards' placeholder while the prices load: three cards (the default
 * pack count) with the loaded card's blocks at their loaded heights, so the grid
 * — and everything under it — stays put when the real packs replace it.
 */
function PricingPacksSkeleton() {
  return (
    <div className="pricing-packs" aria-hidden="true" data-testid="pricing-skeleton">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={index === 1 ? "pricing-pack pricing-pack--skeleton is-lifted" : "pricing-pack pricing-pack--skeleton"}
        >
          <div className="pricing-pack-head">
            <span className="skeleton pricing-skel-tier" />
          </div>
          <div className="pricing-pack-credits">
            <span className="skeleton pricing-skel-figure" />
            <span className="skeleton pricing-skel-unit" />
          </div>
          <div className="pricing-pack-cost">
            <span className="skeleton pricing-skel-price" />
            <span className="skeleton pricing-skel-rate" />
          </div>
          <span className="skeleton pricing-skel-button" />
          <div className="pricing-pack-includes">
            <span className="skeleton pricing-skel-label" />
            <div className="pricing-skel-lines">
              {[0, 1, 2].map((line) => (
                <span key={line} className="skeleton pricing-skel-line" />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
